import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableCollectionBinding, useCollectionBinding } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const validTours = useMemo(
    () => (tours || []).filter((tour) => tour.virtual_tour_url),
    [tours],
  );

  return (
    <section className="bg-background py-8 md:py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="virtualtourembed.tours" label="Virtual Tours">
          {validTours.length > 0 ? (
            <div
              className={
                validTours.length > 1
                  ? 'grid grid-cols-1 gap-8 md:grid-cols-2'
                  : 'grid grid-cols-1'
              }
            >
              {validTours.map((tour, index) => {
                const title = tour.virtual_tour_title || `Tour ${index + 1}`;
                return (
                  <div
                    key={tour.virtual_tour_url || index}
                    className="relative h-[320px] w-full overflow-hidden rounded-lg border border-border bg-muted md:h-[420px] lg:h-[480px]"
                  >
                    <iframe
                      src={tour.virtual_tour_url || undefined}
                      title={title}
                      className="absolute inset-0 h-full w-full border-0"
                      allow="fullscreen; vr"
                      allowFullScreen
                      loading={index < 2 ? 'eager' : 'lazy'}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted">
              <p className="font-body text-muted-foreground">
                No virtual tours are available for this property.
              </p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourEmbed;
